import type { ComponentContentSchema } from '@vibesites/runtime';
import { useContentToken, EditableToken, useEditMode } from '@vibesites/runtime/react';

/**
 * NudgeCTA — an optional "call us to schedule a tour" nudge bar that sits
 * directly under the Hero.
 *
 * Most Dietz Property Group properties will NOT use this, so it is gated behind
 * a boolean-style toggle token (`nudgecta.enabled`). The toggle is a text token
 * whose value is either `"enabled"` or `"disabled"` (default `"disabled"`).
 *
 * Visibility rules:
 *  - Toggle disabled + NOT in edit mode  → render nothing at all.
 *  - Toggle disabled + in edit mode       → render an editor-only hint telling
 *    the author to flip the token to "enabled".
 *  - Toggle enabled                       → render the live nudge CTA.
 *
 * Every piece of copy (message, button label, phone number) is tokenized, and
 * the styling pulls exclusively from the property's design tokens (primary
 * color + on-primary text), so it inherits each property's brand colors.
 */
export function NudgeCTA() {
  const editMode = useEditMode();
  const isEditMode = editMode?.isEditMode ?? false;

  // Boolean-style toggle. Stored as text: "enabled" / "disabled".
  // Defaults for all tokens live in contentSchema; the schema-aware runtime
  // derives them automatically, so we don't repeat them here.
  const enabledRaw = useContentToken('nudgecta.enabled');
  const isEnabled = (enabledRaw || '').trim().toLowerCase() === 'enabled';

  const message = useContentToken('nudgecta.message');
  const buttonLabel = useContentToken('nudgecta.button_label');
  const phone = useContentToken('nudgecta.phone');

  // Build a dialable tel: href from the (possibly formatted) phone string.
  const telHref = `tel:${(phone || '').replace(/[^\d+]/g, '')}`;

  // Disabled + not editing → contribute nothing to the page.
  if (!isEnabled && !isEditMode) {
    return null;
  }

  // Disabled + editing → editor-only hint with the toggle inline-editable.
  if (!isEnabled) {
    return (
      <section className="nudge-section nudge-section--placeholder" aria-hidden="true">
        <div className="nudge-container nudge-container--placeholder">
          <p className="nudge-placeholder-text">
            Nudge CTA is hidden. Change the token to <strong>enabled</strong> to
            show the nudge CTA.
          </p>
          <EditableToken
            path="nudgecta.enabled"
            as="div"
            className="nudge-toggle-chip"
          >
            Toggle: {enabledRaw || 'disabled'}
          </EditableToken>
        </div>

        <style>{nudgeStyles}</style>
      </section>
    );
  }

  // Enabled → the live nudge CTA.
  return (
    <section className="nudge-section">
      <div className="nudge-container">
        <EditableToken path="nudgecta.message" as="p" className="nudge-message">
          {message}
        </EditableToken>

        <a className="nudge-btn" href={telHref} aria-label={`${buttonLabel}: ${phone}`}>
          <svg
            className="nudge-btn-icon"
            viewBox="0 0 24 24"
            width="18"
            height="18"
            fill="currentColor"
            aria-hidden="true"
            focusable="false"
          >
            <path d="M6.62 10.79a15.05 15.05 0 0 0 6.59 6.59l2.2-2.2a1 1 0 0 1 1.02-.24c1.12.37 2.33.57 3.57.57a1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.24.2 2.45.57 3.57a1 1 0 0 1-.24 1.02l-2.21 2.2Z" />
          </svg>
          <EditableToken path="nudgecta.button_label" as="span" className="nudge-btn-label">
            {buttonLabel}:
          </EditableToken>
          <EditableToken path="nudgecta.phone" as="span" className="nudge-btn-phone">
            {phone}
          </EditableToken>
        </a>
      </div>

      <style>{nudgeStyles}</style>
    </section>
  );
}

const nudgeStyles = `
  .nudge-section {
    width: 100%;
    /* Soft tint of the property's primary color for the band background. */
    background: color-mix(in srgb, var(--vibe-color-primary) 10%, white);
  }
  .nudge-container {
    max-width: 80rem;
    margin: 0 auto;
    padding: 1.75rem 1.5rem;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    gap: 1rem 1.5rem;
  }
  .nudge-message {
    margin: 0;
    font-family: var(--vibe-font-heading);
    font-weight: 700;
    font-size: clamp(1.05rem, 1.4vw, 1.35rem);
    color: var(--vibe-color-text);
    text-align: center;
  }
  .nudge-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1.5rem;
    border-radius: 9999px;
    background: var(--vibe-color-primary);
    color: var(--vibe-color-text-on-primary);
    font-family: var(--vibe-font-body);
    font-weight: 700;
    font-size: 0.95rem;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    text-decoration: none;
    white-space: nowrap;
    transition: filter 0.15s ease, transform 0.15s ease;
  }
  .nudge-btn:hover {
    filter: brightness(0.92);
    transform: translateY(-1px);
  }
  .nudge-btn-icon {
    flex-shrink: 0;
  }
  .nudge-btn-label,
  .nudge-btn-phone {
    display: inline;
  }

  /* Editor-only placeholder shown when the toggle is disabled. */
  .nudge-section--placeholder {
    background: color-mix(in srgb, var(--vibe-color-primary) 6%, white);
    border-top: 1px dashed color-mix(in srgb, var(--vibe-color-primary) 40%, white);
    border-bottom: 1px dashed color-mix(in srgb, var(--vibe-color-primary) 40%, white);
  }
  .nudge-container--placeholder {
    flex-direction: column;
    gap: 0.5rem;
  }
  .nudge-placeholder-text {
    margin: 0;
    font-family: var(--vibe-font-body);
    font-size: 0.9rem;
    color: var(--vibe-color-text-muted);
    text-align: center;
  }
  .nudge-toggle-chip {
    font-family: var(--vibe-font-body);
    font-size: 0.8rem;
    font-weight: 600;
    color: var(--vibe-color-primary);
    background: color-mix(in srgb, var(--vibe-color-primary) 14%, white);
    border-radius: 9999px;
    padding: 0.25rem 0.75rem;
  }

  @media (max-width: 640px) {
    .nudge-container {
      flex-direction: column;
      padding: 1.25rem 1rem;
    }
  }
`;

export const contentSchema: ComponentContentSchema = {
  slot: 'NudgeCTA',
  displayName: 'Nudge CTA',
  tokens: {
    'nudgecta.enabled': {
      label: 'Enable Nudge CTA (enabled / disabled)',
      type: 'text',
      defaultValue: 'disabled',
    },
    'nudgecta.message': {
      label: 'Nudge Message',
      type: 'text',
      defaultValue: 'Please call us to schedule an appointment to tour',
    },
    'nudgecta.button_label': {
      label: 'Button Label',
      type: 'text',
      defaultValue: 'Call Now',
    },
    'nudgecta.phone': {
      label: 'Phone Number',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      defaultValue: '(614) 866-9141',
    },
  },
};

(NudgeCTA as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default NudgeCTA;
